import { computed, ref } from 'vue';
import { ProductClientService, ProductSpotModel, PurchaseOptionType } from '@/types/productServerApi';
import utilities from '@/shared/utilities.service';
import useProductDetailData from '@/shared/product/details/useProductDetailData';
import pageStore from '@/shared/content/page.store';
import useProductClickAndCollect from '@/shared/product/purchase/ui/useProductClickAndCollect';

const isUpsellEnabled = ref<boolean>(false);
const upsellItems = ref<ProductSpotModel[]>([] as ProductSpotModel[]);
const { storeId } = useProductClickAndCollect();
const mainProductPurchaseOptionType = ref<PurchaseOptionType | null>(null);

export default function useProductDetailUpsell() {
    const { variantId } = useProductDetailData();
    const shouldShowUpsellItems = computed<boolean>(() => upsellItems.value.length > 0);

    const getUpsellItems = async(): Promise<ProductSpotModel[]> => {
        const basketId = pageStore.basketId;
        const responseWithProductIds = await ProductClientService.getUpSellProducts(variantId.value, basketId)
            .then(utilities.unwrapModel);
        const responseWithProducts = await ProductClientService.getProductSpotsBySku(responseWithProductIds, true).then(utilities.unwrapModel);
        upsellItems.value = filteredUpsellItems(responseWithProducts) ?? [] as ProductSpotModel[];
        return upsellItems.value;
    };

    const filteredUpsellItems = (productList: ProductSpotModel[] | undefined) => {
        if (!productList) {
            return undefined;
        }

        const filteredList: ProductSpotModel[] = [];

        productList.forEach((product) => {
            if (productIsDirectlyPurchasable(product)) {
                filteredList.push(product);
            }
        });
        return filteredList;
    };

    const productIsDirectlyPurchasable = (product: ProductSpotModel): boolean => {
        if (product.purchaseOptions.length === 0) {
            return false;
        }

        const hasSinglePurchaseOptionAndIsClickAndCollect = product.purchaseOptions.length === 1 && product.purchaseOptions[0].type === PurchaseOptionType.ClickAndCollect;
        return hasSinglePurchaseOptionAndIsClickAndCollect ? !!storeId.value : true;
    };

    const maybeInitUpsellItems = async(purchaseOptionType: PurchaseOptionType) => {
        if (isUpsellEnabled.value) {
            await getUpsellItems();
            mainProductPurchaseOptionType.value = purchaseOptionType;
        }
    };

    const clearUpsellItems = () => {
        upsellItems.value.length = 0;
        mainProductPurchaseOptionType.value = null;
    };

    const setIsUpsellEnabled = (enabled: boolean) => {
        isUpsellEnabled.value = enabled;
    };

    return {
        upsellItems,
        isUpsellEnabled,
        shouldShowUpsellItems,
        setIsUpsellEnabled,
        clearUpsellItems,
        maybeInitUpsellItems,
        mainProductPurchaseOptionType,
    };
}
